import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { AiSnippetEnum } from "@/enum/ai-enrichment.enum";

import { ColumnConfig } from "../../../../List/schema/columnConfig";

import { FewShotExample } from "../schema/aiSnippet";

export type SnippetFormMode = "new" | "existing" | "modified";

export type SnippetFormValues = {
	name: string;
	prompt: string;
	promptType: AiSnippetEnum;
	allowedValues: string[];
	fewShotExamples: FewShotExample[];
};

const emptyValues: SnippetFormValues = {
	name: "",
	prompt: "",
	promptType: AiSnippetEnum.BEST_RESPONSE,
	allowedValues: [],
	fewShotExamples: [],
};

export const valuesFromColumn = (column?: ColumnConfig | null): SnippetFormValues => {
	if (!column) {
		return emptyValues;
	}

	const config = column.config ?? {};

	return {
		name: column.name ?? "",
		prompt: typeof config.prompt === "string" ? config.prompt : "",
		promptType: (config.promptType as AiSnippetEnum) || AiSnippetEnum.BEST_RESPONSE,
		allowedValues: Array.isArray(config.allowedValues) ? [...config.allowedValues] : [],
		fewShotExamples: Array.isArray(config.fewShotExamples)
			? config.fewShotExamples.map((e: FewShotExample) => ({
					leadContext: e.leadContext ?? "",
					idealOutput: e.idealOutput ?? "",
				}))
			: [],
	};
};

const sameSnippet = (a: SnippetFormValues, b: SnippetFormValues): boolean => {
	if (a.name !== b.name) {
		return false;
	}

	if (a.prompt !== b.prompt) {
		return false;
	}

	if (a.promptType !== b.promptType) {
		return false;
	}

	if (a.allowedValues.length !== b.allowedValues.length) {
		return false;
	}

	for (let i = 0; i < a.allowedValues.length; i++) {
		if (a.allowedValues[i] !== b.allowedValues[i]) {
			return false;
		}
	}

	if (a.fewShotExamples.length !== b.fewShotExamples.length) {
		return false;
	}

	for (let i = 0; i < a.fewShotExamples.length; i++) {
		if (a.fewShotExamples[i].leadContext !== b.fewShotExamples[i].leadContext) {
			return false;
		}

		if (a.fewShotExamples[i].idealOutput !== b.fewShotExamples[i].idealOutput) {
			return false;
		}
	}

	return true;
};

/**
 * Owns the AI snippet modal's form state and the new/existing/modified mode
 * machine. The mode is derived from whether a template is selected and
 * whether the current form values differ from the original snapshot.
 */
export const useAiSnippetForm = (initialColumn?: ColumnConfig | null) => {
	const initialValuesRef = useRef<SnippetFormValues>(valuesFromColumn(initialColumn));
	const [selectedColumn, setSelectedColumn] = useState<ColumnConfig | null>(initialColumn ?? null);

	const form = useForm<SnippetFormValues>({
		defaultValues: initialValuesRef.current,
	});

	const { watch, reset, setValue, handleSubmit, formState } = form;
	const values = watch();

	const isDirty = useMemo(() => !sameSnippet(values, initialValuesRef.current), [values]);

	const mode: SnippetFormMode = useMemo(() => {
		if (!selectedColumn) {
			return "new";
		}

		return isDirty ? "modified" : "existing";
	}, [selectedColumn, isDirty]);

	const startNew = useCallback(
		(draftValues?: Partial<SnippetFormValues>) => {
			setSelectedColumn(null);
			const next = { ...emptyValues, ...draftValues };

			initialValuesRef.current = next;
			reset(next);
		},
		[reset]
	);

	/**
	 * Imperative loader used by the modal when it opens (or reopens) with a
	 * specific column. Unlike `selectColumn`, this *always* resets the form to
	 * the column's values even when the same column reference is passed —
	 * which is what we need when the modal closes and reopens for the same
	 * snippet after edits were discarded.
	 */
	const loadColumn = useCallback(
		(column: ColumnConfig | null) => {
			const next = valuesFromColumn(column);

			setSelectedColumn(column);
			initialValuesRef.current = next;
			reset(next);
		},
		[reset]
	);

	// Re-clicking the already-selected snippet keeps in-progress edits; only a
	// genuine selection change snapshots the new column and resets the form.
	const selectColumn = useCallback(
		(column: ColumnConfig | null) => {
			if (column?.id != null && column.id === selectedColumn?.id) {
				return;
			}

			loadColumn(column);
		},
		[selectedColumn?.id, loadColumn]
	);

	const discard = useCallback(() => {
		reset(initialValuesRef.current);
	}, [reset]);

	const setName = useCallback(
		(name: string) => setValue("name", name, { shouldDirty: true, shouldValidate: true }),
		[setValue]
	);

	const setPrompt = useCallback(
		(prompt: string) => setValue("prompt", prompt, { shouldDirty: true, shouldValidate: true }),
		[setValue]
	);

	const setPromptType = useCallback(
		(promptType: AiSnippetEnum) =>
			setValue("promptType", promptType, { shouldDirty: true, shouldValidate: true }),
		[setValue]
	);

	const setAllowedValues = useCallback(
		(allowedValues: string[]) =>
			setValue("allowedValues", allowedValues, { shouldDirty: true, shouldValidate: true }),
		[setValue]
	);

	const setFewShotExamples = useCallback(
		(fewShotExamples: FewShotExample[]) =>
			setValue("fewShotExamples", fewShotExamples, { shouldDirty: true, shouldValidate: true }),
		[setValue]
	);

	return {
		form,
		values,
		mode,
		isDirty,
		formState,
		handleSubmit,
		selectedColumn,
		selectColumn,
		loadColumn,
		startNew,
		discard,
		setName,
		setPrompt,
		setPromptType,
		setAllowedValues,
		setFewShotExamples,
	};
};

export type UseAiSnippetFormReturn = ReturnType<typeof useAiSnippetForm>;
